import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiEdit2, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import { AlertMsg } from "../../../shared/components/alert-msg";
import { LoadingItem } from "../../../shared/components/loading";
import { appConstants } from "../../../redux/action-constants/inapp.constants";
import { AttributeTemplatesPageProps } from "../../../types/app/attributeTemplateTypes";
import {
  mapAttributeTemplateToCreatePayload,
  mapAttributeTemplateToUpdatePayload,
} from "../../../utils/attributeTemplatePayload";
import AttributeTemplateDrawer from "./AttributeTemplateDrawer";
import AttributeTemplateFormDrawer from "./AttributeTemplateFormDrawer";
import {
  AttributeTemplateItem,
  normalizeAttributeTemplate,
} from "./AttributeTemplateTypes";
import "./AttributeTemplateManager.scss";

type AttributeTemplateManagerProps = Pick<
  AttributeTemplatesPageProps,
  | "getAttributeTemplatesAction"
  | "getAttributeTemplatesRequest"
  | "getAttributeTemplateAction"
  | "getAttributeTemplateRequest"
  | "createAttributeTemplateAction"
  | "createAttributeTemplateRequest"
  | "updateAttributeTemplateAction"
  | "updateAttributeTemplateRequest"
  | "deleteAttributeTemplateAction"
  | "deleteAttributeTemplateRequest"
>;

const LIST_QUERY = "includeInactive=true";

const formatCreatedAt = (createdAt: string) => {
  const parsedDate = new Date(createdAt);

  if (Number.isNaN(parsedDate.getTime())) {
    return "-";
  }

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getAttributePreview = (
  attributes: AttributeTemplateItem["attributes"],
) => {
  const preview = attributes.slice(0, 3);
  const remaining = attributes.length - preview.length;

  return {
    preview,
    remaining,
  };
};

const AttributeTemplateRow = ({
  template,
  onView,
  onEdit,
  onDelete,
}: {
  template: AttributeTemplateItem;
  onView: (template: AttributeTemplateItem) => void;
  onEdit: (template: AttributeTemplateItem) => void;
  onDelete: (template: AttributeTemplateItem) => void;
}) => {
  const { preview, remaining } = getAttributePreview(template.attributes);

  return (
    <tr className="attribute-template-row">
      <td className="attribute-template-name-cell">
        <div className="attribute-template-name-wrap">
          <button
            type="button"
            className="attribute-template-name-button"
            onClick={() => onView(template)}
          >
            {template.name || "-"}
          </button>
          <div className="attribute-template-description">
            {template.description || "-"}
          </div>
        </div>
      </td>
      <td className="attribute-template-attributes-cell">
        <div className="attribute-template-badges" aria-label="Attribute names">
          {preview.map((attribute) => (
            <span className="attribute-template-badge" key={attribute.id}>
              {attribute.name}
            </span>
          ))}
          {remaining > 0 && (
            <span className="attribute-template-badge muted">+{remaining} more</span>
          )}
        </div>
      </td>
      <td className="attribute-template-status-cell">
        <span
          className={`attribute-template-status-badge ${
            template.isActive ? "active" : "inactive"
          }`}
        >
          {template.isActive ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="attribute-template-product-count-cell">
        <span className="attribute-template-metric">{template.productCount}</span>
      </td>
      <td className="attribute-template-created-at-cell">
        <span className="attribute-template-date">
          {formatCreatedAt(template.createdAt)}
        </span>
      </td>
      <td className="attribute-template-actions-cell">
        <div className="attribute-template-actions">
          <button
            type="button"
            aria-label={`Edit ${template.name || "template"}`}
            onClick={() => onEdit(template)}
          >
            <FiEdit2 size={16} />
          </button>
          <button
            type="button"
            aria-label={`Delete ${template.name || "template"}`}
            className="delete-action"
            onClick={() => onDelete(template)}
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
};

const AttributeTemplateManager = ({
  pageProps,
}: {
  pageProps: AttributeTemplateManagerProps;
}) => {
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">(
    "all",
  );
  const [drawerMode, setDrawerMode] = useState<"view" | "create" | "edit" | null>(
    null,
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateToDelete, setTemplateToDelete] =
    useState<AttributeTemplateItem | null>(null);
  const [deleteConfirmationValue, setDeleteConfirmationValue] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const filterRefs = useRef<
    Record<"all" | "active" | "inactive", HTMLButtonElement | null>
  >({
    all: null,
    active: null,
    inactive: null,
  });
  const [underlineStyle, setUnderlineStyle] = useState({
    left: 0,
    width: 0,
  });

  useEffect(() => {
    pageProps.getAttributeTemplatesAction("CLEAR");
    pageProps.getAttributeTemplateAction("CLEAR");
    pageProps.createAttributeTemplateAction("CLEAR");
    pageProps.updateAttributeTemplateAction("CLEAR");
    pageProps.deleteAttributeTemplateAction("CLEAR", "");
  }, []);

  useEffect(() => {
    pageProps.getAttributeTemplatesAction(LIST_QUERY);
  }, []);

  useEffect(() => {
    if (
      drawerMode === "create" &&
      pageProps.createAttributeTemplateRequest.request_status ===
        appConstants.CREATE_ATTRIBUTE_TEMPLATE_SUCCESS
    ) {
      setSuccessMessage("Attribute template created successfully.");
      pageProps.getAttributeTemplatesAction(LIST_QUERY);
      pageProps.createAttributeTemplateAction("CLEAR");
      setDrawerMode(null);
      setSelectedTemplateId(null);
    }
  }, [pageProps.createAttributeTemplateRequest.request_status, drawerMode]);

  useEffect(() => {
    if (
      drawerMode === "edit" &&
      pageProps.updateAttributeTemplateRequest.request_status ===
        appConstants.UPDATE_ATTRIBUTE_TEMPLATE_SUCCESS
    ) {
      setSuccessMessage("Attribute template updated successfully.");
      pageProps.getAttributeTemplatesAction(LIST_QUERY);
      pageProps.updateAttributeTemplateAction("CLEAR");
      setDrawerMode(null);
      setSelectedTemplateId(null);
    }
  }, [
    pageProps.updateAttributeTemplateRequest.request_status,
    drawerMode,
  ]);

  useEffect(() => {
    if (
      templateToDelete &&
      pageProps.deleteAttributeTemplateRequest.request_status ===
        appConstants.DELETE_ATTRIBUTE_TEMPLATE_SUCCESS
    ) {
      setSuccessMessage(
        `${templateToDelete.name || "Attribute template"} deleted successfully.`,
      );
      pageProps.getAttributeTemplatesAction(LIST_QUERY);
      pageProps.deleteAttributeTemplateAction("CLEAR", "");
      setTemplateToDelete(null);
      setDeleteConfirmationValue("");
      if (selectedTemplateId === templateToDelete.id) {
        setDrawerMode(null);
        setSelectedTemplateId(null);
      }
    }
  }, [
    pageProps.deleteAttributeTemplateRequest.request_status,
    templateToDelete,
    selectedTemplateId,
  ]);

  const listResponse = pageProps.getAttributeTemplatesRequest.request_data?.response;
  const listStatus = pageProps.getAttributeTemplatesRequest.request_status;
  const listLoading = pageProps.getAttributeTemplatesRequest.is_request_processing;
  const detailStatus = pageProps.getAttributeTemplateRequest.request_status;
  const detailLoading = pageProps.getAttributeTemplateRequest.is_request_processing;
  const detailTemplateResponse =
    pageProps.getAttributeTemplateRequest.request_data?.response;

  const templates = useMemo(() => {
    const results = Array.isArray(listResponse) ? listResponse : [];
    return results.map((item) => normalizeAttributeTemplate(item));
  }, [listResponse]);

  const selectedListTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) || null,
    [selectedTemplateId, templates],
  );

  const selectedTemplate = useMemo(() => {
    if (
      detailStatus === appConstants.GET_ATTRIBUTE_TEMPLATE_SUCCESS &&
      detailTemplateResponse
    ) {
      return normalizeAttributeTemplate(detailTemplateResponse);
    }

    return selectedListTemplate;
  }, [detailStatus, detailTemplateResponse, selectedListTemplate]);

  const filteredTemplates = useMemo(() => {
    if (activeFilter === "active") {
      return templates.filter((template) => template.isActive);
    }

    if (activeFilter === "inactive") {
      return templates.filter((template) => !template.isActive);
    }

    return templates;
  }, [activeFilter, templates]);

  const openViewDrawer = (template: AttributeTemplateItem) => {
    setSuccessMessage("");
    setSelectedTemplateId(template.id);
    setDrawerMode("view");
    pageProps.getAttributeTemplateAction("CLEAR");
    pageProps.getAttributeTemplateAction("", template.id);
  };

  const openCreateDrawer = () => {
    setSuccessMessage("");
    setSelectedTemplateId(null);
    setDrawerMode("create");
    pageProps.createAttributeTemplateAction("CLEAR");
    pageProps.updateAttributeTemplateAction("CLEAR");
  };

  const openEditDrawer = (template: AttributeTemplateItem) => {
    setSuccessMessage("");
    setSelectedTemplateId(template.id);
    setDrawerMode("edit");
    pageProps.updateAttributeTemplateAction("CLEAR");
    pageProps.getAttributeTemplateAction("CLEAR");
    pageProps.getAttributeTemplateAction("", template.id);
  };

  const closeDrawer = () => {
    setDrawerMode(null);
    setSelectedTemplateId(null);
    pageProps.getAttributeTemplateAction("CLEAR");
    pageProps.createAttributeTemplateAction("CLEAR");
    pageProps.updateAttributeTemplateAction("CLEAR");
  };

  const returnToTemplateView = () => {
    if (!selectedTemplateId) {
      closeDrawer();
      return;
    }

    setDrawerMode("view");
    pageProps.getAttributeTemplateAction("CLEAR");
    pageProps.getAttributeTemplateAction("", selectedTemplateId);
  };

  const closeDeleteModal = () => {
    if (pageProps.deleteAttributeTemplateRequest.is_request_processing) {
      return;
    }

    setTemplateToDelete(null);
    setDeleteConfirmationValue("");
    pageProps.deleteAttributeTemplateAction("CLEAR", "");
  };

  const handleConfirmDelete = () => {
    if (!templateToDelete?.id) {
      return;
    }

    pageProps.deleteAttributeTemplateAction("DELETE", templateToDelete.id, false);
  };

  useLayoutEffect(() => {
    const updateUnderlineStyle = () => {
      const currentButton = filterRefs.current[activeFilter];

      if (!currentButton) {
        return;
      }

      setUnderlineStyle({
        left: currentButton.offsetLeft,
        width: currentButton.offsetWidth,
      });
    };

    updateUnderlineStyle();
    window.addEventListener("resize", updateUnderlineStyle);

    return () => window.removeEventListener("resize", updateUnderlineStyle);
  }, [activeFilter]);

  const deleteConfirmationLabel = templateToDelete?.name || "";
  const isDeleteConfirmationMatched =
    deleteConfirmationValue.trim() === deleteConfirmationLabel;

  const formFeedback =
    drawerMode === "create" &&
    pageProps.createAttributeTemplateRequest.request_status ===
      appConstants.CREATE_ATTRIBUTE_TEMPLATE_FAILURE
      ? {
          type: "error" as const,
          message:
            pageProps.createAttributeTemplateRequest.request_data?.error ||
            "Unable to create attribute template.",
        }
      : drawerMode === "edit" &&
          pageProps.updateAttributeTemplateRequest.request_status ===
            appConstants.UPDATE_ATTRIBUTE_TEMPLATE_FAILURE
        ? {
            type: "error" as const,
            message:
              pageProps.updateAttributeTemplateRequest.request_data?.error ||
              "Unable to update attribute template.",
          }
        : undefined;

  const isFormSubmitting =
    pageProps.createAttributeTemplateRequest.is_request_processing ||
    pageProps.updateAttributeTemplateRequest.is_request_processing;

  return (
    <div className="attribute-template-manager">
      <div className="attribute-template-header">
        <div className="attribute-template-header-copy">
          <h2>Attribute templates</h2>
          <p>
            Organize reusable product attribute sets for categories and product
            families.
          </p>
        </div>

        <div className="attribute-template-header-actions">
          <div className="attribute-template-total-card">
            <span>Total attribute templates</span>
            <strong>{templates.length}</strong>
          </div>

          <button
            type="button"
            className="attribute-template-add-btn"
            onClick={openCreateDrawer}
          >
            <FiPlus size={16} />
            Add attribute template
          </button>
        </div>
      </div>

      {successMessage && <AlertMsg type="success" message={successMessage} />}

      {!drawerMode &&
        listStatus === appConstants.GET_ATTRIBUTE_TEMPLATES_FAILURE && (
          <AlertMsg
            type="error"
            message={pageProps.getAttributeTemplatesRequest.request_data?.error}
          />
        )}

      {!templateToDelete &&
        pageProps.deleteAttributeTemplateRequest.request_status ===
          appConstants.DELETE_ATTRIBUTE_TEMPLATE_FAILURE && (
          <AlertMsg
            type="error"
            message={pageProps.deleteAttributeTemplateRequest.request_data?.error}
          />
        )}

      <div
        className="attribute-template-switch"
        role="tablist"
        aria-label="Template status filter"
      >
        <button
          ref={(node) => {
            filterRefs.current.all = node;
          }}
          type="button"
          className={`attribute-template-switch-btn ${
            activeFilter === "all" ? "active" : ""
          }`}
          onClick={() => setActiveFilter("all")}
        >
          All
        </button>
        <button
          ref={(node) => {
            filterRefs.current.active = node;
          }}
          type="button"
          className={`attribute-template-switch-btn ${
            activeFilter === "active" ? "active" : ""
          }`}
          onClick={() => setActiveFilter("active")}
        >
          Active
        </button>
        <button
          ref={(node) => {
            filterRefs.current.inactive = node;
          }}
          type="button"
          className={`attribute-template-switch-btn ${
            activeFilter === "inactive" ? "active" : ""
          }`}
          onClick={() => setActiveFilter("inactive")}
        >
          Inactive
        </button>
        <span
          className="attribute-template-underline"
          style={{ left: underlineStyle.left, width: underlineStyle.width }}
        />
      </div>

      {listLoading && <LoadingItem />}

      {!listLoading && (
        <div className="attribute-template-table-wrap">
          <table className="attribute-template-table">
            <thead>
              <tr>
                <th>Attribute template</th>
                <th>Attributes</th>
                <th>Status</th>
                <th>Product count</th>
                <th>Created at</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((template) => (
                <AttributeTemplateRow
                  key={template.id}
                  template={template}
                  onView={openViewDrawer}
                  onEdit={openEditDrawer}
                  onDelete={setTemplateToDelete}
                />
              ))}
            </tbody>
          </table>

          {listStatus === appConstants.GET_ATTRIBUTE_TEMPLATES_SUCCESS &&
            filteredTemplates.length === 0 && (
              <div className="attribute-template-empty-state">
                <p>No attribute templates found.</p>
              </div>
            )}
        </div>
      )}

      {drawerMode === "view" && detailLoading && (
        <div className="attribute-template-form-overlay">
          <LoadingItem />
        </div>
      )}

      {drawerMode === "view" &&
        !detailLoading &&
        detailStatus === appConstants.GET_ATTRIBUTE_TEMPLATE_FAILURE && (
          <div className="attribute-template-form-overlay">
            <div className="attribute-template-delete-modal">
              <AlertMsg
                type="error"
                message={
                  pageProps.getAttributeTemplateRequest.request_data?.error ||
                  "Unable to load attribute template details."
                }
              />
            </div>
          </div>
        )}

      <AttributeTemplateDrawer
        show={
          drawerMode === "view" &&
          !detailLoading &&
          detailStatus !== appConstants.GET_ATTRIBUTE_TEMPLATE_FAILURE &&
          !!selectedTemplate
        }
        template={selectedTemplate}
        onClose={closeDrawer}
        onEdit={selectedTemplate ? () => openEditDrawer(selectedTemplate) : undefined}
        onDelete={() =>
          selectedTemplate ? setTemplateToDelete(selectedTemplate) : undefined
        }
      />

      <AttributeTemplateFormDrawer
        show={drawerMode === "create" || drawerMode === "edit"}
        mode={drawerMode === "create" ? "create" : "edit"}
        template={drawerMode === "create" ? null : selectedTemplate}
        onClose={closeDrawer}
        onBack={returnToTemplateView}
        isSubmitting={isFormSubmitting}
        onSubmit={(template) => {
          if (drawerMode === "create") {
            pageProps.createAttributeTemplateAction(
              mapAttributeTemplateToCreatePayload(template),
            );
            return;
          }

          pageProps.updateAttributeTemplateAction(
            mapAttributeTemplateToUpdatePayload(template),
          );
          return;
        }}
        feedback={formFeedback}
      />

      {templateToDelete && (
        <div className="attribute-template-form-overlay">
          <div className="attribute-template-delete-modal">
            <div className="attribute-template-delete-hero">
              <div className="attribute-template-delete-badge">DEL</div>
            </div>

            <div className="attribute-template-delete-header">
              <h3>Delete attribute template</h3>
              <button
                type="button"
                onClick={closeDeleteModal}
                className="close-button"
                disabled={pageProps.deleteAttributeTemplateRequest.is_request_processing}
                aria-label="Close delete confirmation"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="attribute-template-delete-body">
              {pageProps.deleteAttributeTemplateRequest.request_status ===
                appConstants.DELETE_ATTRIBUTE_TEMPLATE_FAILURE && (
                <AlertMsg
                  type="error"
                  message={
                    pageProps.deleteAttributeTemplateRequest.request_data?.error
                  }
                />
              )}

              <p className="attribute-template-delete-subtext">
                This action is permanent and cannot be undone.
                <br />
                The template will be removed from the list of reusable attributes.
              </p>

              <div className="attribute-template-delete-impact">
                <h4>What will happen?</h4>
                <div className="attribute-template-delete-impact-list">
                  <div className="attribute-template-delete-impact-item">
                    <span className="attribute-template-impact-icon">
                      <FiAlertCircle size={14} />
                    </span>
                    <p>
                      The "{templateToDelete.name}" attribute template will be
                      permanently deleted.
                    </p>
                  </div>
                  <div className="attribute-template-delete-impact-item">
                    <span className="attribute-template-impact-icon">
                      <FiAlertCircle size={14} />
                    </span>
                    <p>
                      Any products using this template would need a replacement
                      attribute set.
                    </p>
                  </div>
                  <div className="attribute-template-delete-impact-item">
                    <span className="attribute-template-impact-icon">
                      <FiAlertCircle size={14} />
                    </span>
                    <p>Related reports or filters may no longer reference it.</p>
                  </div>
                </div>
              </div>

              <div className="attribute-template-delete-confirmation">
                <label htmlFor="delete-attribute-template-confirmation">
                  Type "{deleteConfirmationLabel}" to confirm deletion
                </label>
                <input
                  id="delete-attribute-template-confirmation"
                  type="text"
                  value={deleteConfirmationValue}
                  onChange={(event) => setDeleteConfirmationValue(event.target.value)}
                  placeholder={`Type "${deleteConfirmationLabel}" here...`}
                  disabled={pageProps.deleteAttributeTemplateRequest.is_request_processing}
                />
              </div>

              <div className="attribute-template-delete-actions">
                <button
                  type="button"
                  className="attribute-template-delete-ghost-btn"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="attribute-template-delete-danger-btn"
                  onClick={handleConfirmDelete}
                  disabled={
                    !isDeleteConfirmationMatched ||
                    pageProps.deleteAttributeTemplateRequest.is_request_processing
                  }
                >
                  {pageProps.deleteAttributeTemplateRequest.is_request_processing
                    ? "Deleting..."
                    : "Delete template"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributeTemplateManager;
